'use client';

import { Image, ImageCircleItem, LoadingDark, Padding, Typography } from '@musinsa/mds';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import type { FC } from 'react';
import { twMerge } from 'tailwind-merge';
import type { PropsWithStyles } from '../../common';
import { TrackingDataProvider } from '../../common/components/TrackingDataProvider';
import { getCdnUrl } from '../../common/utils/getCdnUrl';
import { curatorQueries } from '../../query-factory';
import { MyShopSnsLinks } from './MyShopSnsLinks';

export const MyShopProfile: FC<PropsWithStyles> = ({ className, ...props }) => {
  const userInfoQuery = useQuery({
    ...curatorQueries.profile.userInfo,
  });

  const myProfileQuery = useQuery({
    ...curatorQueries.profile.profileBySnsTitle(userInfoQuery.data?.snsTitle ?? ''),
    enabled: Boolean(userInfoQuery.data?.snsTitle),
  });

  const isPending = userInfoQuery.isPending || myProfileQuery.isPending;

  return (
    <Padding
      className={twMerge('flex flex-col', isPending && 'min-h-[64px]', className)}
      x="4"
      top="3"
      bottom="1"
      {...props}
    >
      <div className="flex items-center gap-[16px]">
        <div className="p-[4px]! flex items-center justify-center">
          <ImageCircleItem size="56" className="flex-shrink-0">
            <Image
              src={
                isPending || !myProfileQuery.data?.profileImage
                  ? getCdnUrl('/images/profile/common/default.png')
                  : `${myProfileQuery.data?.profileImage}?width=112&q=75&format=webp`
              }
              alt="profile"
            />
          </ImageCircleItem>
        </div>
        <div
          className={clsx(
            'flex flex-col gap-[2px] flex-1 min-w-0',
            isPending && 'items-center justify-center w-full',
          )}
        >
          {isPending ? (
            <LoadingDark size="size24x24" />
          ) : (
            <>
              <Typography variant="body13pxMed">{myProfileQuery.data?.nickname}</Typography>

              <Typography variant="body13pxReg" wordBreak="breakAll">
                {myProfileQuery.data?.shopDescription}
              </Typography>

              {myProfileQuery.data?.snsLinks && (
                <TrackingDataProvider
                  data={{
                    page_name: 'curator_shop_manage',
                  }}
                >
                  <MyShopSnsLinks snsLinks={myProfileQuery.data?.snsLinks} />
                </TrackingDataProvider>
              )}
            </>
          )}
        </div>
      </div>
    </Padding>
  );
};
